import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Monitor, Palette, Layers, FileText, Sparkles } from "lucide-react";

const services = [
  {
    icon: Monitor,
    title: "Website Design",
    description: "Premium, responsive websites that captivate your audience and drive conversions. Every pixel crafted with purpose.",
    features: ["Custom Design", "Responsive Development", "SEO Optimization", "Performance Tuning"]
  },
  {
    icon: Layers,
    title: "UI/UX Design",
    description: "Intuitive interfaces and seamless user experiences that delight users and achieve business objectives.",
    features: ["User Research", "Wireframing", "Prototyping", "Usability Testing"]
  },
  {
    icon: Palette,
    title: "Brand Identity",
    description: "Distinctive brand systems that tell your story and create lasting impressions in your market.",
    features: ["Logo Design", "Visual Identity", "Brand Guidelines", "Marketing Collateral"]
  },
  {
    icon: FileText,
    title: "Landing Pages",
    description: "High-converting landing pages designed to capture attention and drive specific actions.",
    features: ["Conversion Focused", "A/B Testing Ready", "Fast Loading", "Mobile Optimized"]
  },
  {
    icon: Sparkles,
    title: "Custom Solutions",
    description: "Tailored digital solutions for unique challenges. From complex platforms to specialized applications.",
    features: ["Custom Development", "API Integration", "Database Design", "Ongoing Support"]
  }
];

const process = [
  {
    step: "01",
    title: "Discovery",
    description: "We dive deep into understanding your brand, goals, and audience to establish a solid foundation."
  },
  {
    step: "02",
    title: "Strategy",
    description: "Based on insights gathered, we develop a comprehensive strategy that aligns with your objectives."
  },
  {
    step: "03",
    title: "Design",
    description: "Our designers create stunning visuals that bring your vision to life with meticulous attention to detail."
  },
  {
    step: "04",
    title: "Development",
    description: "We build your solution using modern technologies, ensuring performance and scalability."
  },
  {
    step: "05",
    title: "Launch",
    description: "After thorough testing, we launch your project and ensure a smooth transition to production."
  }
];

export default function Services() {
  return (
    <div className="bg-[#050505] min-h-screen pt-24 md:pt-32" data-testid="services-page">
      {/* Header */}
      <section className="section-padding pb-16">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <p className="text-[#C5A059] uppercase tracking-[0.3em] text-xs mb-4">
              Our Services
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl text-white mb-6">
              Design <span className="italic">Excellence</span>
            </h1>
            <p className="text-[#A1A1A1] text-base md:text-lg leading-relaxed">
              We offer a comprehensive suite of design services tailored to elevate 
              your brand and create meaningful digital experiences.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-padding pt-0" data-testid="services-list">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.8 }}
                className={`bg-[#0F0F0F] p-8 md:p-12 border border-white/5 hover:border-[#C5A059]/30 transition-colors group ${
                  index === 4 ? "md:col-span-2" : ""
                }`}
                data-testid={`service-${service.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="flex items-start justify-between mb-6">
                  <div className="p-3 border border-white/10 text-[#C5A059]">
                    <service.icon size={24} />
                  </div>
                  <ArrowUpRight 
                    size={20} 
                    className="text-[#A1A1A1] group-hover:text-[#C5A059] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all" 
                  />
                </div>
                <h3 className="font-heading text-2xl md:text-3xl text-white mb-4 group-hover:text-[#C5A059] transition-colors">
                  {service.title}
                </h3>
                <p className="text-[#A1A1A1] text-base leading-relaxed mb-6">
                  {service.description}
                </p>
                <ul className="grid grid-cols-2 gap-3">
                  {service.features.map((feature, idx) => (
                    <li
                      key={idx}
                      className="text-[#A1A1A1] text-sm flex items-center gap-2"
                    >
                      <span className="w-1 h-1 rounded-full bg-[#C5A059]" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section-padding border-t border-white/5" data-testid="process-section">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <p className="text-[#C5A059] uppercase tracking-[0.3em] text-xs mb-4">
              How We Work
            </p>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-white">
              Our <span className="italic">Process</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-8 md:gap-4">
            {process.map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.8 }}
                className="text-center md:text-left"
                data-testid={`process-step-${item.step}`}
              >
                <p className="font-heading text-5xl text-[#C5A059]/20 mb-4">
                  {item.step}
                </p>
                <h3 className="font-heading text-xl text-white mb-3">
                  {item.title}
                </h3>
                <p className="text-[#A1A1A1] text-sm leading-relaxed">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding border-t border-white/5">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <p className="text-[#C5A059] uppercase tracking-[0.3em] text-xs mb-4">
                Why Choose Us
              </p>
              <h2 className="font-heading text-3xl md:text-4xl text-white mb-6">
                Design that <span className="italic">delivers</span>
              </h2>
              <div className="space-y-6">
                {[
                  { title: "Premium Quality", desc: "Every project receives meticulous attention to detail and craftsmanship." },
                  { title: "Strategic Approach", desc: "Design decisions backed by research and aligned with business goals." },
                  { title: "Responsive Support", desc: "Direct communication and quick turnarounds throughout the project." },
                  { title: "Results Driven", desc: "We measure success by the impact we create for your business." }
                ].map((item, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="w-1 bg-[#C5A059] flex-shrink-0" />
                    <div>
                      <h4 className="text-white font-medium mb-1">{item.title}</h4>
                      <p className="text-[#A1A1A1] text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="aspect-square bg-[#0F0F0F] overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=800&q=80"
                  alt="Design excellence"
                  className="w-full h-full object-cover opacity-80"
                />
              </div>
              <div className="absolute -bottom-4 -left-4 w-32 h-32 border border-[#C5A059]/30" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding border-t border-white/5">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-[#A1A1A1] uppercase tracking-[0.3em] text-xs mb-6">
              Ready to get started?
            </p>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-white mb-8">
              Let's discuss your <span className="italic text-[#C5A059]">project</span>
            </h2>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://wa.me/918006521008"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary inline-flex items-center justify-center gap-3"
                data-testid="services-whatsapp-cta"
              >
                Chat on WhatsApp
                <ArrowUpRight size={16} />
              </a>
              <Link
                to="/contact"
                className="btn-secondary inline-flex items-center justify-center gap-3"
                data-testid="services-contact-cta"
              >
                Contact Us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
